import { datadogRum } from '@datadog/browser-rum';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js/pure';
import type { ReactChildren, ReactElement, VoidFunctionComponent } from 'react';
import type { Stripe } from '@stripe/stripe-js';

import type { InjectedRouter, RouterState } from 'react-router';
import type { Params } from 'react-router/lib/Router';
import ApplicationMobileWarning from './components/ApplicationMobileWarning';
import DialogManager from './components/DialogManager';
import StatusBanner from './components/generic/StatusBanner';
import JobNotificationProvider from './components/JobNotificationHub/Provider';
import Toaster from './components/Toaster/Toaster';
import DialogContextProvider from './contexts/DialogContext';
import ToastContextProvider from './contexts/ToastContext';
import { window } from './globals';
import * as utils from './utils';
import type { MessageOptions } from './components/modals/ModalPlanSelector';
import RouterContextProvider from './contexts/RouterContext';
import { Provider as PortalUrlProvider } from './components/shared/publicPortal/context/portalUrlContext';
import { instrumentFetch } from './utils/tracing';
import { useOnMount, useOnUnmount } from './hooks';
import { usePlanSelectorContext } from './contexts/PlanSelectorContext';

type ApplicationProps = {
  children: ReactChildren;
  location: RouterState['location'];
  router: InjectedRouter & { params: Params};
};

//  Loads the stripe.js script tag asynchronously
//  stripePromise is passed to Elements in cloud envs where a stripe_key is present
const stripePromise = window.stripe_key && !window.on_prem
  ? loadStripe(window.stripe_key)
  : null as unknown as Stripe;

const enableDatadogRUM = () => {
  if (!utils.isTrue(window.datadogRUM?.enabled)) {
    return;
  }
  instrumentFetch(); // this must be called before datadogRum.init
  datadogRum.init({
    version: window.fossa_version,
    ...window.datadogRUM,
  });

  if (window.user) {
    datadogRum.setUser({
      id: `${window.user.id}`,
      name: window.user.username,
      email: window.user.email,
      organizationId: window.user.organizationId,
      organizationTitle: window.user.organization?.title,
    });
  }
};

const enableDevrev = () => {
  if (!window.devrev.appId || !window.plugSDK) {
    return;
  }

  window.plugSDK.init({
    app_id: window.devrev.appId,
    session_token: window.devrev.sessionToken,
  });
};

// Send user id to heap, so we can use it to identify the user in the heap dashboard.
const identifyHeapUser = () => {
  if (window.heap && window.user?.id) {
    window.heap.identify(window.user.id.toString());
    window.heap.addUserProperties({
      email: window.user.email,
      createdAt: window.user.createdAt,
      organizationId: window.user.organizationId,
      organization: window.user.organization?.title,
    });
  }
};

type StripeWrapperProps = {
  children: ReactElement;
};

const StripeWrapper: VoidFunctionComponent<StripeWrapperProps> = ({ children }: StripeWrapperProps) => {
  if (window.stripe_key) {
    return (
      <Elements stripe={stripePromise}>
        {children};
      </Elements>
    );
  }
  return children;
};

const Application: VoidFunctionComponent<ApplicationProps> = ({
  children,
  location,
  router,
}: ApplicationProps) => {
  const { showPlanSelectorModal } = usePlanSelectorContext();

  useOnMount(() => {
    enableDatadogRUM();
    identifyHeapUser();
    enableDevrev();

    window.showPlanSelectorModal = (messageOptions?: MessageOptions) => showPlanSelectorModal(messageOptions);
    utils.showDelightedSurveyAfter24Hours();
  });

  useOnUnmount(() => {
    // @ts-expect-error - TODO - remove this global state - CORE-4179
    delete window.showPlanSelectorModal;
  });

  return (
    <StripeWrapper>
      <PortalUrlProvider>
        <DialogContextProvider>
          <ToastContextProvider>
            <RouterContextProvider router={router} location={location}>
              <JobNotificationProvider>
                {utils.isAuthed() && <ApplicationMobileWarning />}
                <StatusBanner />
                {children}
                <DialogManager />
                <Toaster location={location} />
              </JobNotificationProvider>
            </RouterContextProvider>
          </ToastContextProvider>
        </DialogContextProvider>
      </PortalUrlProvider>
    </StripeWrapper>
  );
};

export default Application;
